"use client";

import { cn } from "@/lib/utils";

import { PipeChip } from "@/components/global/sections/home/_shared";
import { FadeIn } from "@/components/global/utils/FadeIn";

import type { ILeaderboardColumns } from "@/sanity/pages/PageLeaderboardTemplate";

import { LeaderboardBackground } from "./LeaderboardBackground";
import { LeaderboardTable } from "./LeaderboardTable";
import { Podium } from "./Podium";
import { SAMPLE_LEADERBOARD } from "./sample";
import type { LeaderboardEntry } from "./types";

type LeaderboardView = "monthly" | "hall-of-fame";

const TAB_KEYS: LeaderboardView[] = ["monthly", "hall-of-fame"];

export default function Leaderboard({
  chip,
  title,
  subtitle,
  boardTitle,
  tabMonthly,
  tabHallOfFame,
  columns,
  entries = SAMPLE_LEADERBOARD,
  view = "monthly",
  onViewChange,
}: {
  chip?: string;
  title?: string;
  subtitle?: string;
  boardTitle?: string;
  tabMonthly?: string;
  tabHallOfFame?: string;
  columns?: ILeaderboardColumns;
  entries?: LeaderboardEntry[];
  view?: LeaderboardView;
  onViewChange?: (view: LeaderboardView) => void;
}) {
  const tabLabel: Record<LeaderboardView, string | undefined> = {
    monthly: tabMonthly,
    "hall-of-fame": tabHallOfFame,
  };

  return (
    <section className="relative w-full overflow-x-clip bg-black pb-0 pt-[8rem] max-lg:px-[2rem] lg:px-[45rem] lg:pt-[12rem]">
      <LeaderboardBackground />

      <div className="relative z-10 mx-auto flex w-full flex-col items-center gap-[5rem]">
        <div className="flex w-full max-w-[72.25rem] flex-col items-center gap-[3.375rem] text-center lg:gap-[3.125rem]">
          {chip && (
            <FadeIn delay={0}>
              <PipeChip className="m-0">{chip}</PipeChip>
            </FadeIn>
          )}

          <div className="flex flex-col items-center gap-[2.125rem] lg:gap-[1.625rem]">
            {title && (
              <FadeIn delay={0.1}>
                <h2 className="m-0 font-news text-[4.5rem] leading-[1.1] tracking-[-0.25rem] text-white/90 lg:text-[6rem]">
                  {title}
                </h2>
              </FadeIn>
            )}
            {subtitle && (
              <FadeIn delay={0.15}>
                <p className="m-0 max-w-[43rem] font-geist text-[2.25rem] leading-[1.5] text-white opacity-60 [text-wrap:pretty] lg:max-w-[69.75rem]">
                  {subtitle}
                </p>
              </FadeIn>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-[4rem]">
          <Podium top={entries.slice(0, 3)} />

          <FadeIn delay={0.35} className="flex w-full flex-col gap-[4rem]">
            <div className="flex w-full items-center justify-between gap-[1rem] lg:gap-[2.5rem]">
              <p className="m-0 min-w-0 font-geist text-[2.75rem] leading-none tracking-[-0.065rem] text-white">
                {boardTitle}
              </p>

              <div
                role="tablist"
                aria-label="Board period"
                className="flex shrink-0 items-center gap-[0.5rem] rounded-[1.5rem] bg-white/5 p-[0.5rem]"
              >
                {TAB_KEYS.map((key) => {
                  const active = view === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => onViewChange?.(key)}
                      className={cn(
                        "flex min-h-[4.25rem] items-center justify-center whitespace-nowrap rounded-[1rem] px-[2rem] font-geist text-[1.75rem] font-medium transition-colors duration-200",
                        active
                          ? "bg-[#98d8ff] text-black"
                          : "bg-transparent text-white/50 hover:text-white/80",
                      )}
                    >
                      {tabLabel[key]}
                    </button>
                  );
                })}
              </div>
            </div>

            <LeaderboardTable entries={entries} columns={columns} view={view} />
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
